import { useState } from 'react';

import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import GetAppIcon from '@mui/icons-material/GetApp';
import { Box, Divider } from '@mui/material';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import AddForm from './AddForm';
import { isDownloadToInstance } from '../../../constants/extensionConstants';
import { isLast } from '../../../utils/utils';
import Text from '../../elements/text/Text';

const VersionList = ({
  extensionType,
  versions,
  handleDownload,
  ...restProps
}) => {
  const { t } = useTranslation();

  const [selectedVersion, setSelectedVersion] = useState(null);

  const selectVersion = (version) => setSelectedVersion(version);
  const clearSelectedVersion = () => setSelectedVersion(null);

  const versionList = versions.map((ver, i) => (
    <div key={ver.id}>
      <ListItem dense aria-label="version item">
        <ListItemText
          primary={
            <>
              <Text bodyBold>{ver.version}</Text>
              {!isEmpty(ver.supportedSolrVersions) && (
                <Text captionUpperCase>
                  {`${t('ext.supportedSolrVersions', {
                    ns: 'project',
                  })}: ${ver.supportedSolrVersions.join(', ')}`}
                </Text>
              )}
            </>
          }
          secondary={
            <Text
              caption
              color="textSecondary"
              aria-label="version description"
            >
              {ver.description || ''}
            </Text>
          }
        />
        <ListItemSecondaryAction>
          {isDownloadToInstance(extensionType) ? (
            <IconButton
              edge="end"
              aria-label="download to instance"
              title={t('downloadToInstance', { ns: 'buttons' })}
              onClick={() => selectVersion(ver)}
              size="large"
            >
              <CloudDownloadIcon />
            </IconButton>
          ) : (
            <IconButton
              edge="end"
              aria-label="download to local"
              title={t('download', { ns: 'buttons' })}
              onClick={() => handleDownload(ver)}
              size="large"
            >
              <GetAppIcon />
            </IconButton>
          )}
        </ListItemSecondaryAction>
      </ListItem>
      {!isLast(versions, i) && <Divider />}
    </div>
  ));

  const selectedVersionView = (
    <>
      <Box sx={{ my: 1 }}>
        <Text component="span" sx={{ marginRight: 10 }}>
          {t('ext.selectedVersion', {
            ns: 'project',
            version: selectedVersion?.version,
          })}
        </Text>
        <Button
          variant="outlined"
          color="inherit"
          size="small"
          onClick={clearSelectedVersion}
          aria-label="clear version"
        >
          {t('clear', { ns: 'buttons' })}
        </Button>
      </Box>
      <Box>
        <AddForm
          extensionType={extensionType}
          version={selectedVersion}
          {...restProps}
        />
      </Box>
    </>
  );

  return (
    <>
      <List style={{ marginTop: 10 }} aria-label="version list">
        <Text bodyBold>{t('ext.versions', { ns: 'project' })}</Text>
        {!selectedVersion ? versionList : selectedVersionView}
      </List>
    </>
  );
};

VersionList.propTypes = {
  extensionType: PropTypes.string,
  versions: PropTypes.array,
  handleDownload: PropTypes.func,
};

export default VersionList;
